import React, { memo, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Row, Col } from "react-bootstrap";
import { formatNumber, roundOfAcres, Unit } from "../../../helpers/unit";
import LoadingStyle from "../Common/LoadingStyle";
import { WORK_TYPE_LIST, WORK_TYPE_COLOURS_SECONDARY, WORK_TYPE_COLOURS, ACTIVE_WORK_STATUS, COMPLETED_WORK_STATUS } from "../../../constants/index";
import { useCalculateKPI } from "./utils";

const CardHeader = memo(({ shapeRefId }) => {
    // const { showDropdown, setShowDropdown, shapeRefId } = props;

    const tareLoadHistory = useSelector((state) => state.dashboard.tareLoadHistory);
    const fieldListById = useSelector((state) => state.dashboard.field.fieldListById.payload);
    const findLoads = useSelector((state) => state.dashboard.field.fieldLoads.payload).filter((e) => e.shapeRefId === shapeRefId);
    const selectedWorkTypeIds = useSelector((state) => state.dashboard.field.selectedWorkTypeIds);
    const workedAcresById = useSelector((state) => state.dashboard.field.workedAcresById.payload);
    const workedAcresRequest = useSelector((state) => state.dashboard.field.workedAcresById.request);
    const isRequesting = useSelector((state) => state.dashboard.field.fieldLoads.isRequesting);
    const isRequestingWorkInfo = useSelector((state) => state.dashboard.field.workedAcresById.isRequesting);
    const settings = useSelector((state) => state.user.settings);
    const timezoneIdentifier = useSelector((state) => state.farm.currentFarm.timezone_identifier);
    const workTypeList = useSelector((state) => state.dashboard.workTypeList.payload);
    const harvestSummary = useSelector((state) => state.dashboard.harvestSummary);
    const isRequestingHarvestSummary = useSelector((state) => state.dashboard.harvestSummary.isRequesting);
    const isEnableHarvestProjection = useSelector((state) => state.farm.currentFarm.is_harvest_projection_enable);
    const selectedPlanType = useSelector((state) => state.plan.selectedPlanType);

    const shapeDetails = fieldListById[shapeRefId] || null;
    if (!shapeDetails) return null;

    const loadDetails = {
        isRequesting,
        loads: findLoads.length > 0 ? findLoads[0].loads : 0,
        loadCapacity: findLoads.length > 0 ? findLoads[0].loadCapacity : 0,
    };

    const { getWorkedAcres, getAverageYieldDetails } = useCalculateKPI({
        workTypeList,
        workedAcresById,
        shapeRefId,
        loadDetails,
        workedAcresRequest,
        timezoneIdentifier,
    });

    const tareLoadHistoryList = (tareLoadHistory && tareLoadHistory.data) || [];

    const [harvestSummaryList, setHarvestSummaryList] = useState(null);
    let harvestList = harvestSummaryList && harvestSummaryList.hasOwnProperty(fieldListById[shapeRefId].regionDetails.geo_region_uuid) ? harvestSummaryList[fieldListById[shapeRefId].regionDetails.geo_region_uuid] : null;

    if (!shapeDetails) return <></>;

    const unit = useMemo(() => Unit.getInstance(), []);
    let workTypesById = {};
    workTypeList.forEach(e => {
        if (e.list) {
            e.list.forEach(item => { workTypesById[item.work_type_id] = item; });
        } else workTypesById[e.work_type_id] = e;
    });

    const getWorkedPercentage = (workTypeId) => {
        if (isEnableHarvestProjection && workTypeId === 2) {
            return getSummary(harvestList, "percentage");
        } else {
            const workDetails = workedAcresById[`${shapeRefId}_${workTypeId}`]
                ? workedAcresById[`${shapeRefId}_${workTypeId}`] : null;
            const percentage = workDetails ? workDetails.percentage : 0;
            return percentage > 100 ? 100 : unit.toFixedOf(percentage, 0);
        }
    };

    const getLoadCapacity = (value) => {
        value = value && value.toString().includes(",") ? value.replace(/,/g, "") : value;
        return formatNumber(unit.toFixedOf(unit.toWeight(value, 2), 0));
    };

    const getHarvestValue = (key) => {
        const summary = harvestList && harvestList.summary;
        return summary ? summary[key] || 0 : 0;
    };

    const getSummary = (harvestList, type, fixedValue = 0) => {
        if (!harvestList || !harvestList.summary) return 0;

        const summary = harvestList.summary;
        const workedAcreage = getHarvestValue(summary.harvested_acreage !== undefined ? summary.harvested_acreage : summary.harvest_acreage !== undefined ? summary.harvest_acreage : 0);

        const totalAcreage = getHarvestValue(summary.total_acreage !== undefined ? summary.total_acreage : 0);
        const harvestedLoads = getHarvestValue(summary.harvested_loads !== undefined ? summary.harvested_loads : summary.harvest_loads !== undefined ? summary.harvest_loads : 0);

        const loadCapacity = loadDetails && loadDetails.loadCapacity ? loadDetails.loadCapacity : 0;

        switch (type) {
            case "worked":
                return workedAcreage ? unit.toArea(workedAcreage, fixedValue) : 0;

            case "percentage":
                return totalAcreage ? ((unit.toArea(workedAcreage, 0) / unit.toArea(totalAcreage, 0)) * 100).toFixed(0) : 0;

            case "load":
                return harvestedLoads;

            case "average":
                return totalAcreage ? formatNumber(unit.toFixedOf(unit.toWeight(loadCapacity) / totalAcreage, 0)) : 0;

            default:
                return 0;
        }
    };

    const colSpan = selectedWorkTypeIds.length <= 3 ? 2 : 4;

    const renderWorkType = (id) => {
        const isFieldCompleted = shapeDetails.lastWorkStatus.work_status === COMPLETED_WORK_STATUS;
        const isFieldActive = shapeDetails.lastWorkStatus.work_status === ACTIVE_WORK_STATUS;
        const isFieldHaveLoad = Boolean(getWorkedPercentage(id));

        const workedAcres = getSummary(harvestList, "worked", 2);
        const harvestedLoad = getSummary(harvestList, "load");
        const workedPercentage = getWorkedPercentage(id);

        let completedAcres = 0;
        if (id === 6) {
            completedAcres = getWorkedAcres(id, shapeRefId) ? getWorkedAcres(id, shapeRefId) : 0;
        } else {
            completedAcres = isEnableHarvestProjection && id === 2 ? isFieldHaveLoad ? getSummary(harvestList, "worked") :
                getWorkedAcres(id, shapeRefId) : 0;
        }

        const totalAcres = isEnableHarvestProjection ? unit.toArea(getHarvestValue("total_acreage"), 0) : roundOfAcres(shapeDetails.regionDetails.totalAcres);
        const actualCompletedAcres = completedAcres > totalAcres ? totalAcres : completedAcres;

        const displayColor = !isFieldCompleted
            ? {
                "--percentage": `${isFieldHaveLoad ? workedPercentage : 0}%`,
                "--color": WORK_TYPE_COLOURS[id],
                "--secondary-color": WORK_TYPE_COLOURS_SECONDARY[id],
            }
            : null;

        return (
            <Col key={id} className="work-status" style={displayColor}>
                <Row className="h-100">
                    {/* Completed Area */}
                    <Col className={`m-auto ${id === 2 ? `col-md-${colSpan}` : ""}`}>
                        <Row className="value">{isRequestingWorkInfo ? <LoadingStyle /> : actualCompletedAcres}</Row>
                        <Row className="label">{`Completed ${settings.areaShortName}`}</Row>
                    </Col>

                    {/* Load Information */}
                    {(id === 2) && (
                        <Col className={`m-auto average ${id === 2 ? `col-md-${colSpan} ${selectedWorkTypeIds.length <= 3 ? "pl-2 pr-0" : ""}` : ""}`}>
                            <Row className="value">{loadDetails.isRequesting ? <LoadingStyle /> : isEnableHarvestProjection ? loadDetails.loads : loadDetails.loads}</Row>
                            <Row className="label">Loads</Row>
                        </Col>
                    )}

                    {/* Weight Information for Plan Type 2 */}
                    {selectedPlanType === 2 && (id === 2 || id === 6) && (
                        <Col className={`m-auto average ${id === 2 ? `col-md-${colSpan} ${selectedWorkTypeIds.length <= 3 ? "pl-2 pr-0" : ""}` : ""}`}>
                            <Row className="value">{loadDetails.isRequesting ? <LoadingStyle /> : isEnableHarvestProjection ? unit.toFixedOf(harvestedLoad) : getLoadCapacity(loadDetails.loadCapacity)}</Row>
                            <Row className="label">Weight ({settings.weightShortName})</Row>
                        </Col>
                    )}

                    {/* Average Yield - Completed */}
                    {selectedWorkTypeIds.length <= 3 && id === 2 && (
                        <Col className="m-auto average col-md-3 pl-2 pr-0">
                            <Row className="value">{loadDetails.isRequesting ? <LoadingStyle /> : isEnableHarvestProjection ? workedAcres === 0 ? 0 : isFieldHaveLoad ? getAverageYieldDetails(id, shapeDetails, loadDetails.loadCapacity, "completed", tareLoadHistoryList, workedAcres) : 0 : 0}</Row>
                            <Row className="label">
                                <span>
                                    Completed {settings.areaShortName} Avg. Yield ({settings.weightShortName})
                                </span>
                            </Row>
                        </Col>
                    )}

                    {/* Average Yield - Total */}
                    {isFieldCompleted && id === 2 && (
                        <Col className="m-auto average col-md-3 pl-2 pr-0">
                            <Row className="value">{loadDetails.isRequesting ? <LoadingStyle /> : getAverageYieldDetails(id, shapeDetails, loadDetails.loadCapacity, "total", tareLoadHistoryList)}</Row>
                            <Row className="label">
                                <span>
                                    Total {settings.areaShortName} Avg. Yield ({settings.weightShortName})
                                </span>
                            </Row>
                        </Col>
                    )}

                    {/* Percentage Work Completion */}
                    {isFieldActive ? (
                        <Col className={`m-auto ${id === 2 ? `col-md-${colSpan} p-0 pr-1` : ""}`}>
                            <div className="percentage mr-2 mb-3">{isRequestingWorkInfo ? <LoadingStyle /> : isFieldHaveLoad ? `${workedPercentage}%` : id === 6 ? `${getWorkedPercentage(id)}%` : "0%"}</div>
                        </Col>
                    ) : selectedWorkTypeIds.length === 1 && selectedPlanType !== 2 ? (
                        <Col className="m-auto text-right">Marked Complete</Col>
                    ) : null}
                </Row>
            </Col>
        );
    };

    useEffect(() => {
        if (!isRequestingHarvestSummary) {
            setHarvestSummaryList(harvestSummary.payload || {});
        }
    }, [shapeRefId, harvestSummary]);

    return (
        <div className="field-completion-info">
            <Row className="m-0 w-100 h-100">
                {WORK_TYPE_LIST.flatMap((type) => type.list || [type])
                    .filter((type) => selectedWorkTypeIds.includes(type.work_type_id))
                    .map((type) => renderWorkType(type.work_type_id))}
            </Row>
        </div>
    );
});

export default CardHeader;
